/**
 * MixPanel Service for Analytics Tracking
 * 
 * This service provides a centralized way to track user interactions and events
 * throughout the form builder application. It handles MixPanel initialization,
 * user identification, and event tracking with proper error handling and
 * server-side rendering compatibility.
 * 
 * Usage:
 * 1. Inject the service in your component: constructor(private mixPanelService: MixPanelService)
 * 2. Initialize in ngOnInit: this.mixPanelService.initialize()
 * 3. Track events: this.mixPanelService.track('Event Name', { property: 'value' })
 * 
 * The service automatically handles browser/server-side detection and prevents
 * errors when running on the server.
 */
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import mixpanel from 'mixpanel-browser';
import { environment } from '../../../environments/environment';

export interface MixPanelEvent {
  event: string;
  properties?: Record<string, any>;
}

@Injectable({
  providedIn: 'root'
})
export class MixPanelService {
  private isInitialized = false;
  private readonly MIXPANEL_PREFIX = "Website Builder - ";
  private readonly MIXPANEL_TOKEN = environment.MIXPANEL_TOKEN;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  /**
   * Initialize MixPanel
   */
  initialize(): void {
    if (!isPlatformBrowser(this.platformId)) {
      console.log('MixPanel: Skipping initialization on server side');
      return;
    }

    if (this.isInitialized) {
      console.log('MixPanel: Already initialized');
      return;
    }

    try {
      // Initialize MixPanel
      mixpanel.init(this.MIXPANEL_TOKEN, {
        debug: false, // Set to true for development
        track_pageview: true,
        persistence: 'localStorage'
      });

      this.isInitialized = true;
      console.log('MixPanel: Successfully initialized');
    } catch (error) {
      console.error('MixPanel: Failed to initialize', error);
    }
  }

  /**
   * Track an event
   */
  track(event: string, properties?: Record<string, any>): void {
    if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
      console.log('MixPanel: Cannot track event - not in browser or not initialized');
      return;
    }

    try {
      mixpanel.track(event, properties);
      console.log('MixPanel: Tracked event', event, properties);
    } catch (error) {
      console.error('MixPanel: Failed to track event', event, error);
    }
  }

  /**
   * Identify a user
   */
  identify(userId: string, userProperties?: Record<string, any>): void {
    if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
      console.log('MixPanel: Cannot identify user - not in browser or not initialized');
      return;
    }

    try {
      mixpanel.identify(userId);
      if (userProperties) {
        mixpanel.people.set(userProperties);
      }
      console.log('MixPanel: Identified user', userId, userProperties);
    } catch (error) {
      console.error('MixPanel: Failed to identify user', error);
    }
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
      console.log('MixPanel: Cannot set user properties - not in browser or not initialized');
      return;
    }

    try {
      mixpanel.people.set(properties);
      console.log('MixPanel: Set user properties', properties);
    } catch (error) {
      console.error('MixPanel: Failed to set user properties', error);
    }
  }

  /**
   * Track page view
   */
  trackPageView(pageName: string, properties?: Record<string, any>): void {
    this.track(this.MIXPANEL_PREFIX + 'Page View', {
      page_name: pageName,
      ...properties
    });
  }

  /**
   * Track form builder specific events
   */
  trackTemplateSelection(templateId: number): void {
    this.track(this.MIXPANEL_PREFIX + 'Template Selected', {
      template_id: templateId
    });
  }

  trackThemeSelection(theme: string): void {
    this.track(this.MIXPANEL_PREFIX + 'Theme Selected', {
      theme: theme
    });
  }

  trackSectionCompletion(section: string): void {
    this.track(this.MIXPANEL_PREFIX + section + ' Section Completed');
  }



  trackDeployment(): void {
    this.track(this.MIXPANEL_PREFIX + 'Website Published');
  }

  trackSaveForLater(): void {
    this.track(this.MIXPANEL_PREFIX + 'Save For Later');
  }

  /**
   * Reset user identity
   */
  reset(): void {
    if (!isPlatformBrowser(this.platformId) || !this.isInitialized) {
      return;
    }

    try {
      mixpanel.reset();
      console.log('MixPanel: Reset user identity');
    } catch (error) {
      console.error('MixPanel: Failed to reset user identity', error);
    }
  }

  /**
   * Check if MixPanel is initialized
   */
  isReady(): boolean {
    return isPlatformBrowser(this.platformId) && this.isInitialized;
  }
}
